import { Socket } from 'phoenix'
import { prop } from 'ramda'
import React, { useCallback, useEffect, useState } from 'react'

import { generateAcumulator } from '../acumulator/acumulator'
import { getAuthToken, getProjectId } from '../session/sessionStorage'
import { getSubdomain } from '../../packages/kiti/utils/helpers/url'
import { refetchQuery, removeQueryCache, updateQueryCache } from './helpers'

export const socket = new Socket(process.env.REACT_APP_WS_API_URL)
socket.connect()

export const useCRUDSocket = (namespace, queryState, options) => {
  let { dataKey, disableForInsert } = options || {}

  dataKey = dataKey || namespace
  const currentRecords = prop(dataKey, queryState.data) || []

  const token = getAuthToken()
  const projectId = getProjectId()
  const workspace = getSubdomain()

  const topic = `${namespace}:${workspace}:${projectId}`
  const channel = React.useMemo(() => socket.channel(topic, { token }), [topic, token])

  React.useEffect(() => {
    channel.join().receive('ok', () => console.log(`[SOCKET] CRUD WS Connected: ${namespace}`))
    channel.onClose(() => console.log(`[SOCKET] Closing CRUD WS channel: ${namespace}`))

    channel.on('update', data => {
      console.log(`[SOCKET] CRUD WS event: ${namespace} - update`)
      queryState.updateQuery(updateQueryCache(data, dataKey))
    })

    channel.on('remove', data => {
      console.log(`[SOCKET] CRUD WS event: ${namespace} - remove`)
      queryState.updateQuery(removeQueryCache(data, dataKey))
    })

    return () => {
      channel.leave()
    }
  }, [])

  React.useEffect(() => {
    if (disableForInsert) return false
    const run = generateAcumulator(() => refetchQuery(queryState, dataKey))

    const insertRef = channel.on('insert', () => {
      console.log(`[SOCKET] CRUD WS event: ${namespace} - insert`)
      run()
    })

    return () => {
      channel.off('insert', insertRef)
    }
  }, [queryState.page, currentRecords?.length])
}

export const useSimpleSocket = namespace => {
  const token = getAuthToken()
  const projectId = getProjectId()
  const workspace = getSubdomain()
  const topic = `${namespace}:${workspace}:${projectId}`
  const [connectedEvents, setConnectedEvents] = useState([])

  const channel = React.useMemo(() => socket.channel(topic, { token }), [topic, token])

  useEffect(() => {
    channel.join().receive('ok', () => console.log(`[SOCKET] Joined SIMPLE WS Connected: ${namespace}`))
    channel.onClose(() => console.log(`[SOCKET] Closed SIMPLE WS channel: ${namespace}`))

    return () => {
      channel && channel.leave()
    }
  }, [channel, namespace])

  const off = useCallback(
    eventName => {
      if (!connectedEvents.includes(eventName)) return
      channel.off('insert')
      const index = connectedEvents.indexOf(eventName)

      connectedEvents.splice(index, 1)
      setConnectedEvents(connectedEvents)
    },
    [channel, connectedEvents]
  )

  const on = useCallback(
    (eventName, callback) => {
      if (!connectedEvents.includes(eventName)) {
        channel.on(eventName, callback)
        setConnectedEvents([...connectedEvents, eventName])
      }
    },
    [connectedEvents, channel]
  )

  return { on, off }
}
